import type { ChangeEvent, MouseEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useIntl } from 'react-intl'
import { getRouteApi, Link as RouterLink, useNavigate } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { EnvelopeIcon } from '@gto/ui/icons/outline'
import type { InputProps } from '@gto/ui/input'
import { Link } from '@gto/ui/link'
import { Typography } from '@gto/ui/typography'

import { useTokenCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { TextInput } from '@/components/text-input'
import { useAuth } from '@/hooks/use-auth'
import { useFormValidation } from '@/hooks/use-form-validation'
import { getAxiosErrorMessage, getBackUrl, isErrorCodeInResponse } from '@/utils/helpers'
import type { LoginFormData, LoginFormField } from '@/utils/validation'
import { loginSchema } from '@/utils/validation'

const routeApi = getRouteApi('/_auth')

export const RootRoute = () => {
  const navigate = useNavigate()
  const search = routeApi.useSearch()
  const { onLogin, setEmailValidated, emailValidated } = useAuth()

  const { formatMessage } = useIntl()
  const isSmAndLower = useMediaQuery('down', 'sm')

  const { validate, validateField, errors, clearFieldError, isValid } =
    useFormValidation(loginSchema)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<LoginFormData>({
    email: '',
    password: '',
  })

  const { mutate: createToken, isPending } = useTokenCreate({
    mutation: {
      onSuccess: (data) => {
        onLogin(data, () => {
          navigate({
            to: getBackUrl(search.back_url),
            replace: true,
            viewTransition: true,
          }).catch(console.error)
        }).catch(console.error)
      },
      onError: (error: AxiosError) => {
        const errorCode = isErrorCodeInResponse(error.response?.data)
          ? error.response.data.error_code
          : undefined

        setEmailValidated(errorCode !== 'EMAIL_NOT_VERIFIED')

        const err = getAxiosErrorMessage(error)

        setErrorMessage(err || formatMessage({ id: 'auth.login.error' }))
      },
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      const isValidField = validateField(name as LoginFormField, value)
      if (isValidField) {
        clearFieldError(name as LoginFormField)
      }
    }, 300)
  }

  const onSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const isValid = validate(formValues)
    if (isValid) {
      createToken({ data: formValues })
    }
  }

  const getInputProps = (name: LoginFormField): InputProps => ({
    disabled: isPending,
    size: 'medium',
    onChange,
    value: formValues[name],
    name,
  })

  return (
    <FormContainer>
      <div>
        <Typography
          gutterBottom
          variant={isSmAndLower ? 'h5' : 'h4'}
        >
          <FormattedMessage id="auth.login.title" />
        </Typography>
        <Typography
          color="text.secondary"
          variant="body1"
        >
          <FormattedMessage id="auth.login.description" />
        </Typography>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-8 sm:gap-6">
          <TextInput
            className="flex-[1_1_0px]"
            error={errors.email}
            label="common.labels.email"
            input={{
              ...getInputProps('email'),
              type: 'email',
              endAddon: (
                <EnvelopeIcon
                  className="size-6"
                  color="var(--palette-action-active)"
                />
              ),
            }}
          />

          <TextInput
            className="flex-[1_1_0px]"
            error={errors.password}
            label="common.labels.password"
            input={{
              ...getInputProps('password'),
              type: 'password',
            }}
          />
        </div>
        <Link
          viewTransition
          as={RouterLink}
          to="/login/password-reset"
        >
          <FormattedMessage id="auth.login.forgotPassword" />
        </Link>
      </div>
      {errorMessage !== null && (
        <Alert
          heading={errorMessage}
          severity={emailValidated ? 'warning' : 'success'}
          actions={[
            {
              children: <FormattedMessage id="common.controls.verifyEmail" />,

              onClick: () => {
                navigate({
                  to: '/login/verify-email',
                  replace: true,
                  viewTransition: true,

                  search: { email: formValues.email },
                }).catch(console.error)
              },
            },
          ]}
        />
      )}

      <Button
        color="primary"
        disabled={isPending || !isValid || !formValues.email}
        loading={isPending}
        size="large"
        type="submit"
        variant="contained"
        onClick={onSubmit}
      >
        <FormattedMessage id="common.controls.login" />
      </Button>
    </FormContainer>
  )
}
